import { cn } from "cn";
import {
  type ComponentProps,
  type Ref,
  useCallback,
  useLayoutEffect,
  useRef,
} from "react";

/** Which axis the scrollport owns. `"y"` (the default) keeps today's behavior. */
export type BlockScrollAxis = "y" | "x";

/**
 * Which scroll affordance `BlockScroll` renders, on whichever axis it scrolls.
 * - `fade` — CSS-only mask fade on both edges of the axis (shadcn `scroll-fade-y` /
 *   `scroll-fade-x`, or the webapp-defined `scroll-fade-y-reverse` when `reversed`).
 * - `border` — a border line on either edge, independently shown via `data-scrolled`.
 *
 * Both modes read the scrollport's direction from `reversed`.
 */
export type BlockScrollMode = "fade" | "border";

type BlockScrollBaseProps = ComponentProps<"div"> & {
  /** Ref forwarded to the underlying scroll container. */
  ref?: Ref<HTMLDivElement>;

  /** Scroll affordance to render. Defaults to `"fade"`. */
  mode?: BlockScrollMode;
};

/**
 * `axis` picks the scrolling axis; `reversed` says the scrollport is `column-reverse` — a
 * chat thread, which bottom-pins its newest row. Both affordances invert without it:
 * `scrollTop` is `0` at the visual BOTTOM and runs negative upwards, and the scroll
 * timeline the fade rides starts at that bottom.
 *
 * `reversed` is a y-axis-only option: its fade comes from the webapp's
 * `scroll-fade-y-reverse`, which has no horizontal twin, and no `row-reverse` scrollport
 * exists in the repo. The union says so instead of ignoring it at runtime.
 */
export type BlockScrollProps = BlockScrollBaseProps &
  ({ axis?: "y"; reversed?: boolean } | { axis: "x"; reversed?: never });

/** Which physical edges still have content behind them, or `null` when nothing scrolls. */
type ScrolledEdges = "top" | "bottom" | "left" | "right" | "both";

function scrolledEdges(
  element: HTMLElement,
  axis: BlockScrollAxis,
  reversed: boolean,
): ScrolledEdges | null {
  const { offset, viewport, content } =
    axis === "x"
      ? {
          offset: element.scrollLeft,
          viewport: element.clientWidth,
          content: element.scrollWidth,
        }
      : {
          offset: element.scrollTop,
          viewport: element.clientHeight,
          content: element.scrollHeight,
        };

  const maxScroll = content - viewport;
  if (maxScroll <= 0) return null;

  // A `column-reverse` scrollport puts its scroll origin at the BOTTOM: `scrollTop` is `0`
  // there and reaches `-maxScroll` at the top. Verified in Chrome 152; it is also why
  // `ChannelScrollToBottom` watches for a negative threshold.
  const hiddenBefore = reversed
    ? Math.ceil(maxScroll + offset) > 0
    : offset > 0;
  const hiddenAfter = reversed
    ? offset < 0
    : Math.ceil(offset) + viewport < content;

  if (hiddenBefore && hiddenAfter) return "both";
  if (hiddenBefore) return axis === "x" ? "left" : "top";
  if (hiddenAfter) return axis === "x" ? "right" : "bottom";
  return null;
}

export const BlockScroll = ({
  children,
  className,
  ref,
  mode = "fade",
  axis = "y",
  reversed = false,
  ...restProps
}: BlockScrollProps) => {
  const localRef = useRef<HTMLDivElement>(null);
  const isHorizontal = axis === "x";

  const mergedRef = useCallback(
    (node: HTMLDivElement | null) => {
      localRef.current = node;
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    },
    [ref],
  );

  useLayoutEffect(() => {
    const scrollElement = localRef.current;
    if (!scrollElement) return;

    const updateBorders = () => {
      const scrollState = scrolledEdges(scrollElement, axis, reversed);

      if (scrollState) {
        scrollElement.setAttribute("data-scrolled", scrollState);
      } else {
        scrollElement.removeAttribute("data-scrolled");
      }
    };

    updateBorders();
    scrollElement.addEventListener("scroll", updateBorders, { passive: true });

    // Also watch resize since content might change height dynamically
    const resizeObserver = new ResizeObserver(updateBorders);
    resizeObserver.observe(scrollElement);

    return () => {
      scrollElement.removeEventListener("scroll", updateBorders);
      resizeObserver.disconnect();
    };
  }, [axis, reversed]);

  // `overscroll-x-contain` keeps a swipe past the end of a horizontal row from triggering
  // the browser's back-navigation gesture on macOS.
  const overflowClass = isHorizontal
    ? "overflow-x-auto overscroll-x-contain"
    : "overflow-y-auto";

  const fadeClass = isHorizontal
    ? // `scroll-fade-x` is shadcn's, like `scroll-fade-y` — present wherever
      // `@birdwatch/ui/styles.css` is loaded, so this branch has no app-local dependency.
      "scroll-fade-x"
    : reversed
      ? // `scroll-fade-y-reverse` is a webapp-only utility (webapp/src/global.css) — like
        // `border` mode below, this branch renders unstyled in apps whose CSS does not
        // define it. Its forward twin comes from shadcn and is always here.
        "scroll-fade-y-reverse"
      : "scroll-fade-y";

  // `--border-dark-low` is a webapp-only custom color token (webapp/src/global.css) — this
  // mode silently no-ops in apps whose theme doesn't define it. Width is reserved
  // unconditionally (transparent by default) so only the color toggles on scroll — a
  // conditional *-0 width would zero the border out regardless of color.
  const borderClasses = isHorizontal
    ? [
        "border-x border-transparent",
        "data-[scrolled=left]:border-l-border-dark-low",
        "data-[scrolled=both]:border-l-border-dark-low",
        "data-[scrolled=right]:border-r-border-dark-low",
        "data-[scrolled=both]:border-r-border-dark-low",
      ]
    : [
        "border-y border-transparent",
        "data-[scrolled=top]:border-t-border-dark-low",
        "data-[scrolled=both]:border-t-border-dark-low",
        "data-[scrolled=bottom]:border-b-border-dark-low",
        "data-[scrolled=both]:border-b-border-dark-low",
      ];

  return (
    <div
      {...restProps}
      className={cn(
        overflowClass,
        mode === "fade" ? fadeClass : borderClasses,
        className,
      )}
      ref={mergedRef}
    >
      {children}
    </div>
  );
};
